import React, { useState } from 'react';
import { NavigationProps } from '../types/navigation';
import { Button } from '../components/ui/Button';
import { ArrowLeft, Check, Star, ThumbsUp, ThumbsDown, Package } from 'lucide-react';
import { useStore } from '../lib/store';
// Mock products in the box
const PRODUCTS = [{
  id: 'p1',
  name: 'NOCCO Limón',
  brand: 'NOCCO'
}, {
  id: 'p2',
  name: 'Soft Bar Caramel',
  brand: 'Barebells'
}, {
  id: 'p3',
  name: 'Chili Chips',
  brand: 'Estrella'
}, {
  id: 'p4',
  name: 'Vitamin Well Zero',
  brand: 'Vitamin Well'
}];
type Step = 'intro' | 'product-review' | 'complete';
interface ProductReview {
  taste: boolean | null;
  packaging: boolean | null;
  buyAgain: boolean | null;
}
export function ReviewFlow({
  onNavigate
}: NavigationProps) {
  const [step, setStep] = useState<Step>('intro');
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [productReviews, setProductReviews] = useState<Record<string, ProductReview>>({});
  const {
    user,
    reservations,
    completeReview,
    addReviewEntry
  } = useStore();
  const reservationToReview = reservations.find(r => r.status === 'redeemed');
  const currentProduct = PRODUCTS[currentProductIndex];
  const handleAnswer = (value: boolean) => {
    const questionKeys: (keyof ProductReview)[] = ['taste', 'packaging', 'buyAgain'];
    const currentKey = questionKeys[questionIndex];
    // Update the current product's review
    setProductReviews(prev => ({
      ...prev,
      [currentProduct.id]: {
        ...prev[currentProduct.id],
        [currentKey]: value
      }
    }));
    if (questionIndex < 2) {
      setQuestionIndex(prev => prev + 1);
    } else {
      // Save this product's review to the store
      const review = {
        ...productReviews[currentProduct.id],
        [currentKey]: value
      };
      addReviewEntry({
        reservationId: reservationToReview?.id || '',
        userId: user?.phone || '',
        productName: currentProduct.name,
        productBrand: currentProduct.brand,
        taste: review.taste ?? null,
        packaging: review.packaging ?? null,
        buyAgain: value
      });
      if (currentProductIndex < PRODUCTS.length - 1) {
        setCurrentProductIndex(prev => prev + 1);
        setQuestionIndex(0);
      } else {
        setStep('complete');
      }
    }
  };
  const handleComplete = () => {
    if (reservationToReview) {
      completeReview(reservationToReview.id);
    }
    onNavigate('feed');
  };
  if (!reservationToReview && step !== 'complete') {
    return <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" data-id="element-637">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-638">
          <Package className="w-10 h-10 text-gray-400" data-id="element-639" />
        </div>
        <p className="text-gray-600 font-medium mb-2" data-id="element-640">
          Inget att recensera just nu
        </p>
        <p className="text-gray-400 text-sm mb-6" data-id="element-641">
          Hämta en box först, sedan kan du lämna en review.
        </p>
        <Button onClick={() => onNavigate('feed')} variant="secondary" data-id="element-642">
          Tillbaka till Feed
        </Button>
      </div>;
  }
  // --- SUB-COMPONENTS ---
  const IntroView = () => <div className="flex flex-col h-full p-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-643">
      <div className="flex-1 flex flex-col items-center justify-center space-y-8 text-center" data-id="element-644">
        <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-645">
          <Package className="w-12 h-12 text-blue-600" data-id="element-646" />
        </div>
        <div data-id="element-647">
          <h1 className="text-2xl font-black text-gray-900 mb-2" data-id="element-648">
            Dags att recensera!
          </h1>
          <p className="text-gray-500 max-w-xs mx-auto" data-id="element-649">
            Betygsätt de 4 produkterna i din box för att låsa upp nästa drop.
          </p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 w-full max-w-xs" data-id="element-650">
          <p className="text-sm font-bold text-yellow-800" data-id="element-651">
            🔒 Nästa drop är låst
          </p>
          <p className="text-xs text-yellow-700 mt-1" data-id="element-652">
            Slutför alla reviews för att låsa upp.
          </p>
        </div>
      </div>
      <Button onClick={() => setStep('product-review')} className="mt-auto w-full" data-id="element-653">
        Starta Review
      </Button>
    </div>;
  const ProductReviewView = () => {
    const questions = [{
      title: `Gillade du smaken på ${currentProduct.name}?`,
      key: 'taste'
    }, {
      title: `Vad tyckte du om förpackningen?`,
      key: 'packaging'
    }, {
      title: `Skulle du köpa ${currentProduct.name} igen?`,
      key: 'buyAgain'
    }];
    const currentQuestion = questions[questionIndex];
    return <div className="flex flex-col h-full p-6 text-center animate-in slide-in-from-right duration-300" key={`${currentProductIndex}-${questionIndex}`} data-id="element-654">
        <div className="mb-8" data-id="element-655">
          <span className="text-xs font-bold text-blue-500 uppercase tracking-widest" data-id="element-656">
            Produkt {currentProductIndex + 1} av {PRODUCTS.length}
          </span>
          <h2 className="text-2xl font-black text-gray-900 mt-2" data-id="element-657">
            {currentProduct.brand}
          </h2>
          <p className="text-gray-500" data-id="element-658">{currentProduct.name}</p>
        </div>

        <div className="flex-1 flex flex-col justify-center" data-id="element-659">
          <h3 className="text-xl font-bold mb-8" data-id="element-660">{currentQuestion.title}</h3>
          <div className="grid grid-cols-2 gap-4" data-id="element-661">
            <button onClick={() => handleAnswer(false)} className="p-6 rounded-2xl border-2 flex flex-col items-center justify-center space-y-2 border-gray-200 text-gray-400 hover:border-red-500 hover:bg-red-50 hover:text-red-600 transition-all active:scale-95" data-id="element-662">
              <ThumbsDown className="w-8 h-8" data-id="element-663" />
              <span className="font-bold" data-id="element-664">Nej</span>
            </button>
            <button onClick={() => handleAnswer(true)} className="p-6 rounded-2xl border-2 flex flex-col items-center justify-center space-y-2 border-gray-200 text-gray-400 hover:border-green-500 hover:bg-green-50 hover:text-green-600 transition-all active:scale-95" data-id="element-665">
              <ThumbsUp className="w-8 h-8" data-id="element-666" />
              <span className="font-bold" data-id="element-667">Ja</span>
            </button>
          </div>
        </div>

        <div className="mt-auto pt-6" data-id="element-668">
          <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden" data-id="element-669">
            <div className="bg-blue-500 h-full transition-all duration-300" style={{
            width: `${(currentProductIndex * 3 + questionIndex + 1) / (PRODUCTS.length * 3) * 100}%`
          }} data-id="element-670"></div>
          </div>
          <p className="text-xs text-gray-400 mt-2" data-id="element-671">
            Fråga {currentProductIndex * 3 + questionIndex + 1} av{' '}
            {PRODUCTS.length * 3}
          </p>
        </div>
      </div>;
  };
  const CompleteView = () => <div className="flex flex-col h-full p-6 animate-in zoom-in duration-500" data-id="element-672">
      <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6" data-id="element-673">
        <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-2" data-id="element-674">
          <span className="text-4xl" data-id="element-675">🔓</span>
        </div>
        <div data-id="element-676">
          <h1 className="text-2xl font-black text-gray-900 mb-2" data-id="element-677">
            Nästa Drop Upplåst!
          </h1>
          <p className="text-gray-500" data-id="element-678">
            Tack för din feedback! Du kan nu se nästa event i din Feed.
          </p>
        </div>
        <div className="bg-green-50 text-green-800 px-4 py-2 rounded-full font-bold text-sm" data-id="element-679">
          +30 Poäng Intjänade
        </div>
      </div>
      <Button onClick={handleComplete} className="mt-auto w-full" data-id="element-680">
        Visa Nästa Drop
      </Button>
    </div>;
  return <div className="min-h-screen bg-white relative overflow-hidden flex flex-col" data-id="element-681">
      {step !== 'intro' && step !== 'complete' && <div className="p-4 flex items-center" data-id="element-682">
          <button onClick={() => onNavigate('catches')} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-683">
            <ArrowLeft className="w-6 h-6 text-gray-700" data-id="element-684" />
          </button>
        </div>}
      <div className="flex-1" data-id="element-685">
        {step === 'intro' && <IntroView data-id="element-686" />}
        {step === 'product-review' && <ProductReviewView data-id="element-687" />}
        {step === 'complete' && <CompleteView data-id="element-688" />}
      </div>
    </div>;
}